import { StrictMode, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { ClerkProvider } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import App from './App';
import i18n from './i18n';
import { env } from '@/lib/env';
import { initMonitoring } from '@/lib/monitoring';
import { registerServiceWorker, applyUpdate } from '@/lib/serviceWorker';
import { persisterStorage, PERSISTER_CACHE_KEY } from '@/lib/persisterStore';
import { InstallPrompt } from '@/components/InstallPrompt';
import { IOSInstallHint } from '@/components/IOSInstallHint';
import { OfflineBanner } from '@/components/OfflineBanner';
import { ReconnectToast } from '@/components/ReconnectToast';
import './index.css';

initMonitoring();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * IndexedDB-backed persister for the TanStack Query cache (Batch 2
 * Part B, 12 Jun 2026).
 *
 * Wired via `PersistQueryClientProvider` below. The persister snapshots
 * the (filtered) cache to IDB so that on tab reopen — the caravan-park
 * wifi case — chat history bubbles render instantly from disk while
 * the network reconciles in the background.
 *
 * Throttle: writes throttle to once per second. Chat messages stream
 * in burstily; without throttling we'd hit IDB on every cache mutation.
 * The throttle has no observable downside — the in-memory cache is the
 * session source of truth; the persister only matters across reloads.
 *
 * See `lib/persisterStore.ts` for the storage adapter, IDB layout, and
 * silent-degradation policy on IDB failure.
 */
const persister = createAsyncStoragePersister({
  storage: persisterStorage,
  key: PERSISTER_CACHE_KEY,
  throttleTime: 1000,
});

/**
 * Cache buster. Bump this string whenever the persisted cache shape
 * changes in a way that would crash the rehydrated client (e.g.,
 * ChatMessage gains a required field). Bumping invalidates every
 * persisted cache on next load, forcing a clean refetch.
 *
 * Format: `ti-cache-v{n}`. History:
 *   v1 — Batch 2 Part B initial (12 Jun 2026): chat history only,
 *        ChatMessage v2 shape (id, from, text, timestamp, dateLocal,
 *        kind, optional pickName).
 */
const CACHE_BUSTER = 'ti-cache-v1';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

function FallbackError(): JSX.Element {
  return (
    <main className="container-narrow safe-top safe-bottom flex min-h-dvh items-center justify-center">
      <div className="text-center">
        <h1 className="font-display text-2xl text-moor-800">Something's gone wrong.</h1>
        <p className="mt-2 text-slate-700">Please reload the page. We've been notified.</p>
      </div>
    </main>
  );
}

function UpdateBanner({ onApply }: { onApply: () => void }): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-center justify-between gap-3 rounded-2xl bg-moor-700 px-4 py-3 text-sm text-white shadow-lg safe-bottom animate-slide-up"
    >
      <span>A fresh version is ready.</span>
      <button
        type="button"
        onClick={onApply}
        className="rounded-full bg-white/15 px-3 py-1 font-medium transition hover:bg-white/25"
      >
        Reload
      </button>
    </div>
  );
}

/**
 * Wraps ClerkProvider with React Router navigation integration.
 *
 * Clerk uses `routerPush` / `routerReplace` for redirects after sign-in,
 * sign-out, and verification flows. Wiring them to React Router's
 * `useNavigate` avoids full page reloads on auth navigation. Must live
 * INSIDE BrowserRouter so `useNavigate` has the router context.
 */
function ClerkProviderWithRouter({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
    >
      {children}
    </ClerkProvider>
  );
}

/**
 * Provider stack (outermost → innermost):
 *   Sentry.ErrorBoundary → HelmetProvider → I18nextProvider →
 *   PersistQueryClientProvider → BrowserRouter → ClerkProviderWithRouter → App
 *
 * `PersistQueryClientProvider` replaces the plain `QueryClientProvider`
 * (Batch 2 Part B, 12 Jun 2026). It provides the same QueryClient
 * context to children AND rehydrates the persisted cache from IDB
 * before any queries fetch. Children render immediately; queries that
 * have a persisted hit appear instantly, queries without one (or with
 * a stale persisted hit) refetch in the background.
 *
 * I18nextProvider sits above PersistQueryClientProvider so every
 * component tree below can use `useTranslation()`. The i18next
 * instance itself initialises synchronously at module load (see
 * `./i18n` — English is bundled, so `t()` works from first render
 * with no Suspense boundary needed). Non-English language bundles
 * load asynchronously via `addResourceBundle` once their JSON arrives
 * in later phases.
 *
 * Banner mount order (Batch 3, 12 Jun 2026; ReconnectToast added in
 * Phase 6 sub-commit 3, 13 Jun 2026):
 *   `OfflineBanner` and `ReconnectToast` are mounted unconditionally
 *   alongside App, BEFORE the update / install priority chain. Both
 *   are orthogonal to "fresh version ready" and "install this PWA" —
 *   a guest can be offline OR mid-reconnect while an update is
 *   pending, or either while not yet installed. The OfflineBanner
 *   sits at TOP of viewport (amber alarm), the ReconnectToast at
 *   BOTTOM (moor neutral, transient), and the UpdateBanner at bottom
 *   too. The Reconnect / Update overlap is intentional and harmless:
 *   the ReconnectToast is transient (~2 seconds visible), so its
 *   collision window with a persistent UpdateBanner is brief and
 *   self-resolving. Conditional rendering happens inside each
 *   component (returns null when not applicable), not here, so
 *   mount semantics stay simple.
 *
 * `dehydrateOptions.shouldDehydrateQuery` — the filter that decides
 * which queries are persisted. Strict allowlist (not denylist) so a
 * future hook can't accidentally start leaking sensitive data to IDB
 * just by existing — anything not explicitly matched is in-memory
 * only.
 *
 * Currently persisted:
 *   - `['chat', 'history', userId]` where userId is a non-empty
 *     string AND the query is in a `success` state
 *
 * Explicitly NOT persisted:
 *   - `['users', 'me', userId]` (profile + partner) — returns in
 *     ~50ms on a warm CF cache; keeps user PII (name, party
 *     composition, stay dates, partner attribution) off-device
 *   - Picks — town-data with 4hr in-memory staleTime; persisting
 *     would bloat the cache for no real win
 *   - Any in-flight, errored, or empty-key query
 *
 * The userId guard catches the resolving-Clerk window: during the
 * brief tick when Clerk reports "signed in" but `userId` is still
 * null, the queryKey would be `['chat', 'history', null]`. Persisting
 * that would create a null-keyed entry that could later collide with
 * account B's first-render on a shared device — exactly the
 * cross-account leak we structurally fixed in Part A. Filtering here
 * is defence-in-depth on top of the user-scoped query keys.
 */
function Root(): JSX.Element {
  const [updateReady, setUpdateReady] = useState(false);
  // Register SW on first render. Effect-free: registerServiceWorker waits for window 'load'
  // and has its own module-level guard against double registration.
  registerServiceWorker(() => setUpdateReady(true));
  return (
    <Sentry.ErrorBoundary fallback={<FallbackError />}>
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days — matches chat history retention in storage.ts
              buster: CACHE_BUSTER,
              dehydrateOptions: {
                shouldDehydrateQuery: (query) => {
                  const k = query.queryKey;
                  if (!Array.isArray(k) || k.length !== 3) return false;
                  if (k[0] !== 'chat' || k[1] !== 'history') return false;
                  if (typeof k[2] !== 'string' || k[2].length === 0) return false;
                  return query.state.status === 'success';
                },
              },
            }}
          >
            <BrowserRouter>
              <ClerkProviderWithRouter>
                <App />
                {/* OfflineBanner is independent of the update / install priority
                    chain — offline state is orthogonal. Mounted unconditionally;
                    renders null internally when shouldSend is true. */}
                <OfflineBanner />
                {/* ReconnectToast — Phase 6 sub-commit 3. Same orthogonality
                    rationale as OfflineBanner; bottom-of-viewport position
                    deliberately contrasts the top-of-viewport alarm slot.
                    Renders null when not in the recently-recovered window. */}
                <ReconnectToast />
                {/* UpdateBanner takes priority — when a new version is waiting, suppress install banners. */}
                {updateReady ? (
                  <UpdateBanner onApply={applyUpdate} />
                ) : (
                  <>
                    <InstallPrompt />
                    <IOSInstallHint />
                  </>
                )}
              </ClerkProviderWithRouter>
            </BrowserRouter>
          </PersistQueryClientProvider>
        </I18nextProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
